import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Ticket, Calendar, Clock, AlertCircle, CheckCircle2, 
  Plus, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    openTickets: 0,
    upcomingBookings: 0,
    resolvedTickets: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [ticketsRes, bookingsRes] = await Promise.all([
        fetch(`${API}/tickets`, { credentials: 'include' }),
        fetch(`${API}/bookings`, { credentials: 'include' })
      ]);

      if (ticketsRes.ok) {
        const tickets = await ticketsRes.json();
        setRecentTickets(tickets.slice(0, 3));
        setStats(prev => ({
          ...prev,
          openTickets: tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length,
          resolvedTickets: tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length
        }));
      }

      if (bookingsRes.ok) {
        const bookings = await bookingsRes.json();
        setRecentBookings(bookings.slice(0, 3));
        setStats(prev => ({
          ...prev,
          upcomingBookings: bookings.filter(b => b.status === 'pending' || b.status === 'confirmed').length
        }));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'text-yellow-400 bg-yellow-400/10',
      in_progress: 'text-blue-400 bg-blue-400/10',
      resolved: 'text-green-400 bg-green-400/10',
      closed: 'text-gray-400 bg-gray-400/10',
      pending: 'text-yellow-400 bg-yellow-400/10',
      confirmed: 'text-green-400 bg-green-400/10',
      completed: 'text-cyan-400 bg-cyan-400/10',
      cancelled: 'text-red-400 bg-red-400/10'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
          Welcome back, {user?.name?.split(' ')[0] || 'User'}!
        </h1>
        <p className="text-gray-400">Here's an overview of your account activity.</p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {[
          { label: 'Open Tickets', value: stats.openTickets, icon: Ticket, color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
          { label: 'Upcoming Bookings', value: stats.upcomingBookings, icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-500/10' },
          { label: 'Resolved Issues', value: stats.resolvedTickets, icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-400/10' }
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="p-6 rounded-xl neo-card"
              style={{ background: '#0D1224', border: '1px solid rgba(99, 130, 255, 0.12)' }}
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className={`w-12 h-12 rounded-xl ${stat.bg} flex items-center justify-center mb-4 border border-blue-500/5`}>
                <Icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-8"
      >
        <h2 className="text-lg font-semibold text-white mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-4">
          <Link to="/tickets">
            <Button className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:opacity-95 text-white font-semibold shadow-[0_4px_12px_rgba(59,130,246,0.2)] hover:shadow-[0_8px_20px_rgba(59,130,246,0.3)] transition-all border-none" data-testid="quick-new-ticket">
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </Link>
          <Link to="/bookings">
            <Button variant="outline" className="border-blue-500/20 text-white bg-transparent hover:bg-blue-500/5 hover:border-blue-400 transition-colors" data-testid="quick-schedule">
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Service
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Tickets */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="rounded-xl p-6 border"
          style={{ background: '#0D1224', borderColor: 'rgba(99, 130, 255, 0.12)' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Recent Tickets</h2>
            <Link to="/tickets" className="text-blue-400 text-sm hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4 text-blue-400" />
            </Link>
          </div>

          {recentTickets.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No tickets yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentTickets.map((ticket) => (
                <Link
                  key={ticket.ticket_id}
                  to={`/tickets/${ticket.ticket_id}`}
                  className="block p-4 rounded-lg bg-[#080B14]/40 hover:bg-blue-500/5 border border-blue-500/5 hover:border-blue-500/20 transition-all duration-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-medium line-clamp-1">{ticket.subject}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                      {ticket.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>{ticket.category}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(ticket.created_at)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Bookings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="rounded-xl p-6 border"
          style={{ background: '#0D1224', borderColor: 'rgba(99, 130, 255, 0.12)' }}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-white">Upcoming Bookings</h2>
            <Link to="/bookings" className="text-blue-400 text-sm hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4 text-blue-400" />
            </Link>
          </div>

          {recentBookings.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-10 h-10 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-400 text-sm">No upcoming bookings</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentBookings.map((booking) => (
                <div
                  key={booking.booking_id}
                  className="p-4 rounded-lg bg-[#080B14]/40 border border-blue-500/5"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-white font-medium">{booking.package_id}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                      {booking.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(booking.scheduled_date)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
